import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    CheckCircle,
    CreditCard,
    Store,
    User,
    DollarSign,
    Calendar,
    Image as ImageIcon,
    FileText,
    AlertCircle,
    Clock,
    XCircle,
    Car,
    Home,
    MapPin,
    Tag,
    Mail,
    Phone,
    ShieldCheck,
    ExternalLink,
    Info
} from 'lucide-react';
import { useGetMarketplaceTransactionsQuery, useUpdateMarketplaceTransactionStatusMutation } from '../../store/slices/apiSlice';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import toast from 'react-hot-toast';

const MarketplaceTransactionDetails: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const transactionId = id ? parseInt(id) : null;
    const isValidId = transactionId && !isNaN(transactionId) && transactionId > 0;

    const { data: transactionsData, isLoading, error, refetch } = useGetMarketplaceTransactionsQuery({
        page: 1,
        limit: 1000
    });

    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [updateStatus, setUpdateStatus] = useState<'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded'>('completed');
    const [adminNotes, setAdminNotes] = useState('');
    const [markListingSold, setMarkListingSold] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
    const [updateTransactionStatus, { isLoading: isUpdating }] = useUpdateMarketplaceTransactionStatusMutation();

    const transactions = transactionsData?.data?.transactions || [];
    const transaction = transactions.find(t => t.transaction_id === transactionId);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'completed':
                return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Completed</Badge>;
            case 'processing':
                return <Badge variant="warning" className="flex items-center gap-1"><Clock className="w-3 h-3" />Processing</Badge>;
            case 'pending':
                return <Badge variant="warning" className="flex items-center gap-1"><Clock className="w-3 h-3" />Pending</Badge>;
            case 'failed':
                return <Badge variant="danger" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Failed</Badge>;
            case 'cancelled':
                return <Badge variant="default" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Cancelled</Badge>;
            case 'refunded':
                return <Badge variant="default" className="flex items-center gap-1"><AlertCircle className="w-3 h-3" />Refunded</Badge>;
            case 'requires_kyc':
                return <Badge variant="warning" className="flex items-center gap-1"><AlertCircle className="w-3 h-3" />KYC Required</Badge>;
            case 'blocked_threshold':
                return <Badge variant="danger" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Blocked Threshold</Badge>;
            default:
                return <Badge variant="default">{status}</Badge>;
        }
    };

    const formatCurrency = (amount: number, currencySymbol?: string | null) => {
        const symbol = currencySymbol || '₦';
        return `${symbol}${amount.toLocaleString('en-US')}`;
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);

    const handleUpdateStatus = () => {
        if (transaction) {
            setUpdateStatus(transaction.status as any);
            setAdminNotes(transaction.admin_notes || '');
            setMarkListingSold(false);
            setShowUpdateModal(true);
        }
    };

    const handleSaveStatusUpdate = async () => {
        if (!transaction) return;

        try {
            await updateTransactionStatus({
                transaction_id: transaction.transaction_id,
                status: updateStatus,
                admin_notes: adminNotes.trim() || undefined,
                mark_listing_sold: markListingSold
            }).unwrap();
            toast.success('Transaction status updated successfully');
            setShowUpdateModal(false);
            setAdminNotes('');
            setMarkListingSold(false);
            refetch();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to update transaction status');
        }
    };

    const handleViewImage = (imageUrl: string) => {
        setSelectedImageUrl(imageUrl);
        setShowImageModal(true);
    };

    if (!isValidId) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Invalid transaction ID</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error || !transaction) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Error loading transaction. Please try again.</p>
                    <button
                        onClick={() => navigate('/admin/marketplace-transactions')}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    const listing = transaction.listing;
    const user = transaction.user;

    return (
        <div className="min-h-screen bg-gray-50 pb-12">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/marketplace-transactions')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Transaction Details</h1>
                                <p className="text-gray-600 text-sm">Transaction ID: #{transaction.transaction_id}</p>
                            </div>
                        </div>
                        <Button
                            onClick={handleUpdateStatus}
                            variant="primary"
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Update Status
                        </Button>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-4xl mx-auto space-y-6">
                    {/* Transaction Overview Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <CreditCard className="w-5 h-5 text-blue-600" />
                                Transaction Overview
                            </h2>
                            <div>{getStatusBadge(transaction.status)}</div>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100">
                                <label className="text-xs font-semibold uppercase text-blue-600 tracking-wider">Total Amount</label>
                                <p className="mt-1 text-2xl font-bold text-gray-900">
                                    {formatCurrency(transaction.amount, user?.currency_symbol || listing?.currency_symbol)}
                                </p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                                <label className="text-xs font-semibold uppercase text-gray-500 tracking-wider">Payment Method</label>
                                <p className="mt-1 text-lg font-semibold text-gray-900 capitalize">
                                    {transaction.payment_method?.replace('_', ' ') || 'N/A'}
                                </p>
                                {transaction.wallet_amount > 0 && (
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        Wallet: {formatCurrency(transaction.wallet_amount, user?.currency_symbol)}
                                    </p>
                                )}
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                                <label className="text-xs font-semibold uppercase text-gray-500 tracking-wider">Date & Time</label>
                                <div className="mt-1 flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-gray-400" />
                                    <p className="text-sm font-medium text-gray-900">{formatDate(transaction.created_at)}</p>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 pb-6 pt-0 grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-gray-100 mt-2">
                            {transaction.external_reference && (
                                <div className="pt-4">
                                    <label className="text-xs font-medium text-gray-500 uppercase">External Reference</label>
                                    <p className="mt-1 text-sm font-mono font-medium text-gray-900 bg-gray-100 px-3 py-1.5 rounded inline-block">
                                        {transaction.external_reference}
                                    </p>
                                </div>
                            )}
                            {transaction.verified_at && (
                                <div className="pt-4">
                                    <label className="text-xs font-medium text-gray-500 uppercase">Verified At</label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">{formatDate(transaction.verified_at)}</p>
                                    </div>
                                </div>
                            )}
                            {transaction.is_large_transaction && (
                                <div className="pt-4">
                                    <label className="text-xs font-medium text-gray-500 uppercase">Large Transaction Alert</label>
                                    <div className="mt-1">
                                        <Badge variant="warning">Large Transaction Flagged</Badge>
                                    </div>
                                </div>
                            )}
                            {transaction.is_below_min_threshold && (
                                <div className="pt-4">
                                    <label className="text-xs font-medium text-gray-500 uppercase">Threshold Status</label>
                                    <div className="mt-1">
                                        <Badge variant="danger">Below Minimum Threshold</Badge>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Listing Information Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Store className="w-5 h-5 text-purple-600" />
                                Listing Details
                            </h2>
                            {listing?.listing_type && (
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                                    listing.listing_type === 'car'
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-purple-100 text-purple-800'
                                }`}>
                                    {listing.listing_type === 'car' ? <Car className="w-3.5 h-3.5" /> : <Home className="w-3.5 h-3.5" />}
                                    {listing.listing_type}
                                </span>
                            )}
                        </div>
                        {listing ? (
                            <div className="p-6 space-y-6">
                                {/* Listing Title & Main Specs */}
                                <div className="flex flex-col md:flex-row gap-6 items-start">
                                    {listing.images && listing.images.length > 0 ? (
                                        <div className="w-full md:w-48 h-36 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 bg-gray-100">
                                            <img
                                                src={listing.images.find(img => img.is_primary)?.image_path || listing.images[0]?.image_path || ''}
                                                alt={listing.title}
                                                className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                                                onClick={() => handleViewImage(listing.images!.find(img => img.is_primary)?.image_path || listing.images![0]?.image_path || '')}
                                            />
                                        </div>
                                    ) : (
                                        <div className="w-full md:w-48 h-36 rounded-lg border border-gray-200 flex-shrink-0 bg-gray-100 flex items-center justify-center text-gray-400">
                                            <Store className="w-12 h-12" />
                                        </div>
                                    )}

                                    <div className="flex-1 space-y-3">
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900">{listing.title}</h3>
                                            <p className="text-xs text-gray-500">Listing ID: #{listing.listing_id}</p>
                                        </div>

                                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                            {listing.price !== undefined && (
                                                <div className="flex items-center gap-1 font-semibold text-gray-900">
                                                    <Tag className="w-4 h-4 text-gray-400" />
                                                    <span>{formatCurrency(listing.price, listing.currency_symbol || user?.currency_symbol)}</span>
                                                </div>
                                            )}
                                            {listing.location && (
                                                <div className="flex items-center gap-1">
                                                    <MapPin className="w-4 h-4 text-gray-400" />
                                                    <span>{listing.location}</span>
                                                </div>
                                            )}
                                            {listing.status && (
                                                <div className="flex items-center gap-1">
                                                    <span className="text-gray-400">Status:</span>
                                                    <Badge variant={listing.status === 'sold' ? 'success' : 'default'} className="capitalize">
                                                        {listing.status}
                                                    </Badge>
                                                </div>
                                            )}
                                        </div>

                                        {listing.description && (
                                            <p className="text-sm text-gray-600 line-clamp-3 bg-gray-50 p-3 rounded-lg border border-gray-100">
                                                {listing.description}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Car Specific Details */}
                                {listing.car_details && typeof listing.car_details === 'object' && (
                                    <div className="pt-4 border-t border-gray-100">
                                        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
                                            <Car className="w-4 h-4 text-blue-600" />
                                            Vehicle Specifications
                                        </h4>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-100 text-sm">
                                            {Object.entries(listing.car_details).map(([key, val]) => (
                                                val ? (
                                                    <div key={key}>
                                                        <label className="text-xs text-gray-500 uppercase font-medium">{key.replace('_', ' ')}</label>
                                                        <p className="font-semibold text-gray-900 capitalize">{String(val)}</p>
                                                    </div>
                                                ) : null
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* House Specific Details */}
                                {listing.house_details && typeof listing.house_details === 'object' && (
                                    <div className="pt-4 border-t border-gray-100">
                                        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
                                            <Home className="w-4 h-4 text-purple-600" />
                                            Property Specifications
                                        </h4>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-100 text-sm">
                                            {Object.entries(listing.house_details).map(([key, val]) => (
                                                val ? (
                                                    <div key={key}>
                                                        <label className="text-xs text-gray-500 uppercase font-medium">{key.replace('_', ' ')}</label>
                                                        <p className="font-semibold text-gray-900 capitalize">{String(val)}</p>
                                                    </div>
                                                ) : null
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="p-6 text-gray-500 text-sm">
                                Listing information not available for this transaction.
                            </div>
                        )}
                    </div>

                    {/* User Information Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <User className="w-5 h-5 text-emerald-600" />
                                User Information
                            </h2>
                            {user?.user_id && (
                                <span className="text-xs text-gray-500 font-medium">User ID: #{user.user_id}</span>
                            )}
                        </div>
                        {user ? (
                            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="text-xs font-semibold uppercase text-gray-500">Full Name</label>
                                    <p className="mt-1 text-base font-semibold text-gray-900">
                                        {user.name || user.email?.split('@')[0] || 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase text-gray-500">Email Address</label>
                                    <div className="mt-1 flex items-center gap-1.5 text-sm text-gray-900">
                                        <Mail className="w-4 h-4 text-gray-400" />
                                        <span>{user.email || 'N/A'}</span>
                                    </div>
                                </div>
                                {user.mobile_number && (
                                    <div>
                                        <label className="text-xs font-semibold uppercase text-gray-500">Mobile Number</label>
                                        <div className="mt-1 flex items-center gap-1.5 text-sm text-gray-900">
                                            <Phone className="w-4 h-4 text-gray-400" />
                                            <span>{user.mobile_number}</span>
                                        </div>
                                    </div>
                                )}
                                {user.marketplace_kyc_status && (
                                    <div>
                                        <label className="text-xs font-semibold uppercase text-gray-500">Marketplace KYC Status</label>
                                        <div className="mt-1">
                                            <Badge variant={user.marketplace_kyc_status === 'approved' ? 'success' : 'warning'} className="capitalize">
                                                {user.marketplace_kyc_status.replace('_', ' ')}
                                            </Badge>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="p-6 text-gray-500 text-sm">
                                User details not available for this transaction.
                            </div>
                        )}
                    </div>

                    {/* Payment Proof Screenshot */}
                    {transaction.payment_screenshot && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <ImageIcon className="w-5 h-5 text-indigo-600" />
                                    Payment Screenshot Proof
                                </h2>
                            </div>
                            <div className="p-6">
                                <div 
                                    className="relative group cursor-pointer rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition-all bg-gray-100 max-w-xl mx-auto"
                                    onClick={() => handleViewImage(transaction.payment_screenshot!)}
                                >
                                    <div className="min-h-[250px] flex items-center justify-center p-2">
                                        <img
                                            src={transaction.payment_screenshot}
                                            alt="Payment Screenshot"
                                            className="w-full h-auto max-h-[450px] object-contain group-hover:scale-105 transition-transform duration-300 rounded"
                                        />
                                    </div>
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center pointer-events-none">
                                        <ImageIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                </div>
                                <p className="mt-3 text-xs text-gray-500 text-center">Click image to inspect proof in full screen</p>
                            </div>
                        </div>
                    )}

                    {/* Admin Notes */}
                    {transaction.admin_notes && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-amber-600" />
                                    Admin Notes
                                </h2>
                            </div>
                            <div className="p-6">
                                <p className="text-sm text-gray-800 bg-amber-50/50 p-4 rounded-lg border border-amber-100 whitespace-pre-wrap">
                                    {transaction.admin_notes}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Verification Audit */}
                    {transaction.verified_by && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <ShieldCheck className="w-5 h-5 text-emerald-600" />
                                    Verification Audit Log
                                </h2>
                            </div>
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-xs font-semibold uppercase text-gray-500">Verified By Admin</label>
                                    <p className="mt-1 text-sm font-semibold text-gray-900">{transaction.verified_by}</p>
                                </div>
                                {transaction.verified_at && (
                                    <div>
                                        <label className="text-xs font-semibold uppercase text-gray-500">Verified At</label>
                                        <p className="mt-1 text-sm font-medium text-gray-900">{formatDate(transaction.verified_at)}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Update Status Modal */}
            <Modal
                isOpen={showUpdateModal}
                onClose={() => {
                    setShowUpdateModal(false);
                    setAdminNotes('');
                    setMarkListingSold(false);
                }}
                title="Update Transaction Status"
            >
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <select
                            value={updateStatus}
                            onChange={(e) => setUpdateStatus(e.target.value as any)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="processing">Processing</option>
                            <option value="completed">Completed</option>
                            <option value="failed">Failed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="refunded">Refunded</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notes</label>
                        <textarea
                            value={adminNotes}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            rows={4}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter admin notes..."
                        />
                    </div>
                    {updateStatus === 'completed' && transaction.listing && (
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="markSold"
                                checked={markListingSold}
                                onChange={(e) => setMarkListingSold(e.target.checked)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor="markSold" className="ml-2 text-sm font-medium text-gray-700">
                                Mark listing as sold
                            </label>
                        </div>
                    )}
                    <div className="flex justify-end gap-3 pt-4">
                        <Button
                            onClick={() => {
                                setShowUpdateModal(false);
                                setAdminNotes('');
                                setMarkListingSold(false);
                            }}
                            variant="outline"
                            disabled={isUpdating}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveStatusUpdate}
                            variant="primary"
                            disabled={isUpdating}
                            isLoading={isUpdating}
                        >
                            Update Status
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Image Modal */}
            <Modal
                isOpen={showImageModal}
                onClose={() => {
                    setShowImageModal(false);
                    setSelectedImageUrl('');
                }}
                title="Image Lightbox"
            >
                <div className="p-6">
                    <img
                        src={selectedImageUrl}
                        alt="Enlarged preview"
                        className="w-full h-auto rounded-lg max-h-[80vh] object-contain mx-auto"
                    />
                </div>
            </Modal>
        </div>
    );
};

export default MarketplaceTransactionDetails;


